import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import T from 'i18n';
import Button from '@mui/material/Button';

import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  selectAutoSetDoDate,
  selectChangeDocId,
  selectCurrentFolder,
  selectCurrentFolderAgreed,
  selectCurrentCaseId,
  selectParams,
  selectPermissions,
  selectShowExhibitNumber,
} from 'common/selectors';
import { usePrevious } from 'utils/hooks';
import { Controller, useForm } from 'react-hook-form';
import history from 'common/history';
import * as actions from '../../redux/actions';
import isEqual from 'react-fast-compare';
import moment from 'moment';
import { MultiSelectForm, Spinner, TextForm } from 'features/common';
import { Collapse, Divider, IconButton, InputAdornment, TextField } from '@mui/material';
import Split from 'react-split';
import { suggestedRecipientsMenuOptions } from '../../enums';
import classnames from 'classnames';
import { Col, Form, Row } from 'react-bootstrap';
import SubjectLabel from '../labels/SubjectLabel';
import DateFormLabel from '../labels/DateFormLabel';
import DateInput from 'features/common/FormControls/DateInput';
import TimeInput from 'features/common/FormControls/TimeInput';
import AuthorRecipientLabel from '../labels/AuthorRecipientLabel';
import { AddCircleOutline, ExpandLess, ExpandMore } from '@mui/icons-material';
import FileViewer from '../../FileViewer';
import { Modal } from 'features/common/MaterialBasedComponents';
import useFormDirtyState from '../hooks/useFormDirtyState';
import api from 'common/api';

const EXHIBIT_NUMBER_REGEX = /^[A-Za-z]{1,3}-[0-9]+(?:\.[0-9]+)*$/i;
const EXHIBIT_NUMBER_PARTIAL_REGEX = /^[A-Za-z]{0,3}-?[0-9.]*$/i;

const ModalUpdateMeta = ({
  show,
  handleClose,
  handleSave,
  action,
  selectedRows,
  isAllDocuments,
  isDocFinder,
  currentGridList,
  onRowSelected,
}) => {
  const dispatch = useDispatch();

  const { file: alreadyOpenedFile } = useSelector(selectParams);
  const [file, setFile] = useState(selectedRows[selectedRows.length - 1] || {});
  const prevFile = usePrevious(file);
  const [loading, setLoading] = useState(false);
  const [navigationBtnClicked, setNavigationBtnClicked] = useState(false);
  const [currentSelection, setCurrentSelection] = useState(null);

  const [authorVal, setAuthorVal] = useState(file.author || '');
  const [recipientVal, setRecipientVal] = useState(file.recipient || '');
  const [recipientListVal, setRecipientListVal] = useState(file.recipientsList || []);
  const [newRecipientVal, setNewRecipientVal] = useState('');
  const prevRecipientListVal = usePrevious(recipientListVal);
  const [fileNameVal, setFileNameVal] = useState(file.name || file.originalName || '');
  const [showPreview, setShowPreview] = useState(false);
  const [disableActionBtns, setDisableActionBtns] = useState({
    disableNext: false,
    disablePrev: false,
  });
  const [openEntities, setOpenEntities] = useState(false);
  const [exhibitCheckPending, setExhibitCheckPending] = useState(false);
  const [exhibitTaken, setExhibitTaken] = useState(false);
  const [exhibitEdited, setExhibitEdited] = useState(false);
  const [exhibitCheckQueued, setExhibitCheckQueued] = useState(false);
  const [exhibitCheckedValue, setExhibitCheckedValue] = useState('');
  const exhibitCheckTimer = useRef(null);
  const exhibitCheckAbort = useRef(null);
  const exhibitCheckedValueRef = useRef('');
  const exhibitEditedRef = useRef(false);

  const [initialValues, setInitialValues] = useState({});

  const {
    documents: { canUpdatePublicData, canUpdateTeamData },
  } = useSelector(selectPermissions);
  const isAutoSetDocDate = useSelector(selectAutoSetDoDate);
  const canChangeDocId = useSelector(selectChangeDocId);
  const isTrialBookAgreed = useSelector(selectCurrentFolderAgreed);
  const currentFolder = useSelector(selectCurrentFolder);
  const showExhibitNumber = useSelector(selectShowExhibitNumber);
  const currentCaseId = useSelector(selectCurrentCaseId);
  const totalDocuments = currentGridList.length;
  const { cost, ...entitiesCollection } = (file && file.entities) || null;

  const setFileDataInStore = useCallback(
    (f) => dispatch(actions.setCurrentSelectedFile(f)),
    [dispatch],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
    control,
    trigger,
    watch,
    setError,
    clearErrors,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      docDate: file.docDate ? file.docDate.split(' ')[0] || '' : '',
      docTime: file.docDate ? file.docDate.split(' ')[1] || '' : '',
      id: file.id || '',
      tab: file.tab || '',
      startPage: file.startPage || '',
      altDate: file.altDate || '',
      disclosureId: file.disclosureId || '',
      familyId: file.familyId || '',
      discoveryId: file.discoveryId || '',
      externalId: file.externalId || '',
      exhibitNumber: file.exhibitNumber || '',
      description: file.description || '',
      notes: file.notes || '',
    },
    reValidateMode: 'onBlur',
  });

  const watchedFormValues = watch();
  const watchedExhibitNumber = watchedFormValues.exhibitNumber || '';
  const exhibitValue = watchedExhibitNumber.trim();
  const exhibitCheckIncomplete =
    exhibitEdited && !!exhibitValue && exhibitCheckedValue !== exhibitValue;
  const exhibitBlocked =
    exhibitCheckPending ||
    exhibitCheckQueued ||
    exhibitCheckIncomplete ||
    exhibitTaken ||
    !!errors.exhibitNumber;
  const exhibitErrorMessage = errors?.exhibitNumber?.message;
  const hasStartPageValue =
    watchedFormValues.startPage !== '' &&
    watchedFormValues.startPage !== null &&
    watchedFormValues.startPage !== undefined;
  const shouldForceStartPageUpdate = isTrialBookAgreed && !file.agreed && hasStartPageValue;

  const currentValues = {
    // Form values
    docDate: watchedFormValues.docDate || '',
    docTime: watchedFormValues.docTime || '',
    id: watchedFormValues.id || '',
    tab: watchedFormValues.tab || '',
    startPage: watchedFormValues.startPage || '',
    altDate: watchedFormValues.altDate || '',
    disclosureId: watchedFormValues.disclosureId || '',
    familyId: watchedFormValues.familyId || '',
    discoveryId: watchedFormValues.discoveryId || '',
    externalId: watchedFormValues.externalId || '',
    exhibitNumber: watchedFormValues.exhibitNumber || '',
    description: watchedFormValues.description || '',
    notes: watchedFormValues.notes || '',
    // Local state values
    author: authorVal,
    recipient: recipientVal,
    recipientsList: recipientListVal,
    name: fileNameVal,
  };

  const isDirty = useFormDirtyState(initialValues, currentValues);

  const currentGridFile = useMemo(() => {
    if (!Array.isArray(currentGridList)) return null;
    return currentGridList.find((doc) =>
      file.folderFileId
        ? doc.folderFileId === file.folderFileId && doc.id === file.id
        : doc.id === file.id,
    );
  }, [currentGridList, file.folderFileId, file.id]);

  const gridDisplayedTab = useMemo(() => {
    if (!currentGridFile) return '';
    const gridTab = currentGridFile.tab;
    const bundleTab = currentGridFile.bundleLocations?.[0]?.tab;
    return !gridTab && isDocFinder ? bundleTab || gridTab || '' : gridTab || '';
  }, [currentGridFile, isDocFinder]);

  const hasTabValueMismatch = useMemo(
    () => !gridDisplayedTab && !!(currentValues.tab || file.tab),
    [gridDisplayedTab, currentValues.tab, file.tab],
  );

  const isExhibitBlockedImmediate = useCallback(
    (valueOverride) => {
      const value = (valueOverride ?? getValues('exhibitNumber') ?? '').trim();
      const incomplete =
        exhibitEditedRef.current && !!value && exhibitCheckedValueRef.current !== value;
      return (
        exhibitCheckPending ||
        exhibitCheckQueued ||
        exhibitTaken ||
        !!errors.exhibitNumber ||
        incomplete
      );
    },
    [getValues, exhibitCheckPending, exhibitCheckQueued, exhibitTaken, errors.exhibitNumber],
  );

  useEffect(() => {
    // Reset exhibit edited flag when switching files
    setExhibitEdited(false);
    exhibitEditedRef.current = false;
    setExhibitCheckQueued(false);
    setExhibitCheckPending(false);
    setExhibitTaken(false);
    setExhibitCheckedValue('');
    exhibitCheckedValueRef.current = '';
    clearErrors('exhibitNumber');
    if (exhibitCheckTimer.current) {
      clearTimeout(exhibitCheckTimer.current);
      exhibitCheckTimer.current = null;
    }
    if (exhibitCheckAbort.current) {
      exhibitCheckAbort.current.abort();
      exhibitCheckAbort.current = null;
    }
  }, [file?.id, file?.folderFileId, clearErrors]);

  const isAllowedExhibitPartial = useCallback(
    (val) => EXHIBIT_NUMBER_PARTIAL_REGEX.test(val || ''),
    [],
  );

  const sanitizeExhibitValue = useCallback((val) => {
    const upper = (val || '').toUpperCase();
    let out = '';
    let hasDash = false;
    let lettersCount = 0;

    for (let i = 0; i < upper.length; i++) {
      const ch = upper[i];
      if (!hasDash) {
        if (/[A-Z]/.test(ch) && lettersCount < 3) {
          out += ch;
          lettersCount += 1;
          continue;
        }
        if (ch === '-' && lettersCount > 0) {
          out += ch;
          hasDash = true;
          continue;
        }
        // skip anything else before dash
      } else {
        if (/[0-9.]/.test(ch)) {
          out += ch;
        }
        // skip anything else after dash
      }
    }
    // ensure only one dash
    const dashIdx = out.indexOf('-');
    if (dashIdx !== -1) {
      // strip extra dashes if any snuck in
      out = out.slice(0, dashIdx + 1) + out.slice(dashIdx + 1).replace(/-/g, '');
    }
    return out;
  }, []);

  const handleExhibitKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        if (isExhibitBlockedImmediate(e.target.value)) {
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }
      const allowedKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
        'Home',
        'End',
      ];
      if (
        allowedKeys.includes(e.key) ||
        (e.metaKey || e.ctrlKey) ||
        e.key.length !== 1 // allow control keys like Enter already handled elsewhere
      ) {
        return;
      }
      const target = e.target;
      const { selectionStart, selectionEnd, value } = target;
      const nextVal =
        value.slice(0, selectionStart) + e.key + value.slice(selectionEnd ?? selectionStart);
      if (!isAllowedExhibitPartial(nextVal.toUpperCase())) {
        e.preventDefault();
      }
    },
    [isAllowedExhibitPartial, isExhibitBlockedImmediate],
  );

  const handleExhibitPaste = useCallback(
    (e) => {
      const paste = (e.clipboardData || window.clipboardData).getData('text') || '';
      const target = e.target;
      const { selectionStart, selectionEnd, value } = target;
      const nextVal =
        value.slice(0, selectionStart) + paste + value.slice(selectionEnd ?? selectionStart);
      const sanitized = sanitizeExhibitValue(nextVal);
      if (!sanitized.length && nextVal.length) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      setValue('exhibitNumber', sanitized, { shouldDirty: true, shouldValidate: true });
      setExhibitEdited(true);
      exhibitEditedRef.current = true;
      setExhibitCheckQueued(true);
      setExhibitCheckedValue('');
      exhibitCheckedValueRef.current = '';
      if (sanitized !== nextVal.toUpperCase()) {
        setError('exhibitNumber', {
          type: 'pattern',
          message: T.translate('case.exhibitNumberInvalidFormat'),
        });
      } else {
        clearErrors('exhibitNumber');
      }
    },
    [sanitizeExhibitValue, setValue, setError, clearErrors, setExhibitCheckedValue],
  );

  const handleExhibitChange = useCallback(
    (e) => {
      const next = sanitizeExhibitValue(e.target.value);
      setValue('exhibitNumber', next, { shouldDirty: true, shouldValidate: true });
      setExhibitEdited(true);
      exhibitEditedRef.current = true;
      setExhibitCheckQueued(true);
      setExhibitCheckedValue('');
      exhibitCheckedValueRef.current = '';
      if (next !== (e.target.value || '').toUpperCase()) {
        setError('exhibitNumber', {
          type: 'pattern',
          message: T.translate('case.exhibitNumberInvalidFormat'),
        });
      } else {
        clearErrors('exhibitNumber');
      }
    },
    [sanitizeExhibitValue, setValue, setError, clearErrors, setExhibitCheckedValue],
  );

  useEffect(() => {
    const value = (watchedExhibitNumber || '').trim();
    setExhibitTaken(false);

    if (exhibitCheckTimer.current) {
      clearTimeout(exhibitCheckTimer.current);
      exhibitCheckTimer.current = null;
    }
    if (exhibitCheckAbort.current) {
      exhibitCheckAbort.current.abort();
      exhibitCheckAbort.current = null;
    }

    if (!show) {
      setExhibitCheckPending(false);
      setExhibitCheckQueued(false);
      setExhibitCheckedValue('');
      exhibitCheckedValueRef.current = '';
      return;
    }
    if (!value) {
      setExhibitCheckPending(false);
      setExhibitCheckQueued(false);
      setExhibitCheckedValue('');
      exhibitCheckedValueRef.current = '';
      clearErrors('exhibitNumber');
      return;
    }

    if (!EXHIBIT_NUMBER_REGEX.test(value)) {
      setExhibitCheckPending(false);
      setExhibitCheckQueued(false);
      setExhibitTaken(false);
      setExhibitCheckedValue('');
      exhibitCheckedValueRef.current = '';
      setError('exhibitNumber', {
        type: 'pattern',
        message: T.translate('case.exhibitNumberInvalidFormat'),
      });
      return;
    }

    clearErrors('exhibitNumber');
    if (!exhibitEdited) {
      setExhibitCheckPending(false);
      setExhibitCheckQueued(false);
      setExhibitCheckedValue('');
      exhibitCheckedValueRef.current = '';
      return;
    }
    if (!currentCaseId) {
      setExhibitCheckPending(false);
      setExhibitCheckQueued(false);
      setExhibitCheckedValue('');
      exhibitCheckedValueRef.current = '';
      return;
    }

    setExhibitCheckQueued(true);
    exhibitCheckTimer.current = setTimeout(() => {
      const controller = new AbortController();
      exhibitCheckAbort.current = controller;
      setExhibitCheckQueued(false);
      setExhibitCheckPending(true);
      api
        .get(
          `/cases/${currentCaseId}/doc-finder?search=${encodeURIComponent(
            value,
          )}&exhibitNumberOnly=true`,
          { signal: controller.signal },
        )
        .then((res) => {
          if (controller.signal.aborted) return;
          setExhibitCheckPending(false);
          exhibitCheckAbort.current = null;
          const resultsArray = Array.isArray(res?.results) ? res.results : [];
          const hasMatch = resultsArray.length > 0;
          setExhibitTaken(hasMatch);
          setExhibitCheckedValue(value);
          exhibitCheckedValueRef.current = value;
          if (hasMatch) {
            setError('exhibitNumber', {
              type: 'conflict',
              message: T.translate('case.exhibitNumberTaken'),
            });
          } else {
            clearErrors('exhibitNumber');
          }
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          setExhibitCheckPending(false);
          exhibitCheckAbort.current = null;
          setExhibitCheckedValue(value);
          exhibitCheckedValueRef.current = value;
          setError('exhibitNumber', {
            type: 'validate',
            message: T.translate('case.exhibitNumberCheckFailed'),
          });
        });
    }, 500);

    return () => {
      if (exhibitCheckTimer.current) {
        clearTimeout(exhibitCheckTimer.current);
        exhibitCheckTimer.current = null;
      }
      if (exhibitCheckAbort.current) {
        exhibitCheckAbort.current.abort();
        exhibitCheckAbort.current = null;
      }
      setExhibitCheckQueued(false);
    };
  }, [watchedExhibitNumber, show, currentCaseId, clearErrors, setError, exhibitEdited]);

  const selectNewDocument = useCallback(
    (fileId) => {
      currentGridList.forEach((doc) => {
        if (doc.folderFileId === fileId || doc.id === fileId) doc.selected = true;
        else doc.selected = false;
      });
      if (onRowSelected) onRowSelected([currentGridList.filter(({ selected }) => !!selected)]);
    },
    [currentGridList, onRowSelected],
  );

  const handleUserKeyPress = useCallback(
    (e) => {
      if (disableActionBtns.disablePrev && disableActionBtns.disableNext) return;

      if (e.key === 'Enter' && !isDirty && !hasTabValueMismatch) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      setNavigationBtnClicked(false);
      if ((e.ctrlKey || e.metaKey) && e.code === 'ArrowRight') {
        if (isExhibitBlockedImmediate()) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        setCurrentSelection(e.target.name);
        handleSubmit((data) => nextClicked(data))();
        setNavigationBtnClicked(true);
      } else if ((e.ctrlKey || e.metaKey) && e.code === 'ArrowLeft') {
        if (isExhibitBlockedImmediate()) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        setCurrentSelection(e.target.name);
        handleSubmit((data) => prevClicked(data))();
        setNavigationBtnClicked(true);
      } else if (e.key === 'Enter' && isExhibitBlockedImmediate()) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [
      disableActionBtns,
      handleSubmit,
      nextClicked,
      prevClicked,
      isDirty,
      hasTabValueMismatch,
      isExhibitBlockedImmediate,
    ],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);
    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  useEffect(() => {
    if (alreadyOpenedFile) {
      if (alreadyOpenedFile !== file.id)
        history.push(history.location.pathname.replace(/\/files.*/, ``) + `/files/${file.id}`);
    }
  }, [alreadyOpenedFile, file]);

  useEffect(() => {
    if (file.folderFileId !== prevFile.folderFileId || file.id !== prevFile.id) {
      setLoading(true);
      dispatch(
        actions.fetchFileDetails({
          fileId: file.id,
          folderFileId: file.folderFileId,
          folderId: currentFolder.id,
        }),
      ).then((res) => {
        const initialFormData = res.docDate
          ? {
              docDate: res.docDate.split(' ')[0] || '',
              docTime: res.docDate.split(' ')[1] || '',
            }
          : { docDate: '', docTime: '' };

        const formResetData = {
          ...initialFormData,
          id: res.id || '',
          tab: res.tab || '',
          startPage: res.startPage || '',
          altDate: res.altDate || '',
          disclosureId: res.disclosureId || '',
          familyId: res.familyId || '',
          discoveryId: res.discoveryId || '',
          externalId: res.externalId || '',
          exhibitNumber: res.exhibitNumber || '',
          description: res.description || '',
          notes: res.notes || '',
        };

        reset(formResetData);
        setFile(res);

        setFileNameVal(res.name || res.originalName || '');
        setValue('name', res.name || res.originalName || '');
        setAuthorVal(res.author || '');
        setRecipientVal(res.recipient || '');
        setRecipientListVal(res.recipientsList || []);

        setTimeout(() => {
          const initialFormValues = {
            docDate: res.docDate ? res.docDate.split(' ')[0] || '' : '',
            docTime: res.docDate ? res.docDate.split(' ')[1] || '' : '',
            name: res.name || res.originalName || '',
            id: res.id || '',
            tab: res.tab || '',
            startPage: res.startPage || '',
            altDate: res.altDate || '',
            disclosureId: res.disclosureId || '',
            familyId: res.familyId || '',
            discoveryId: res.discoveryId || '',
            externalId: res.externalId || '',
            exhibitNumber: res.exhibitNumber || '',
            author: res.author || '',
            description: res.description || '',
            notes: res.notes || '',
            recipient: res.recipient || '',
            recipientsList: res.recipientsList || [],
          };
          setInitialValues(initialFormValues);
        }, 0);

        setLoading(false);
        const currentFileIdx = currentGridList.findIndex(
          (doc) => doc.folderFileId === file.folderFileId && doc.id === file.id,
        );
        setDisableActionBtns(
          currentFileIdx === 0
            ? { disableNext: false, disablePrev: true }
            : currentFileIdx === currentGridList.length - 1
              ? { disableNext: true, disablePrev: false }
              : { disableNext: false, disablePrev: false },
        );
      });
    }
  }, [file, prevFile, dispatch, currentFolder, currentGridList, reset]);

  const prevClicked = useCallback(
    (data) => {
      if (isExhibitBlockedImmediate(data?.exhibitNumber)) return;
      const currentFileIdx = currentGridList.findIndex(
        (doc) => doc.folderFileId === file.folderFileId && doc.id === file.id,
      );
      if (currentFileIdx > 0) {
        setLoading(true);
        setDisableActionBtns({ disableNext: true, disablePrev: true });
        saveFileData(
          data,
          currentGridList[currentFileIdx - 1].folderFileId ||
            currentGridList[currentFileIdx - 1].id,
          hasTabValueMismatch,
          hasTabValueMismatch ? { ...file, tab: gridDisplayedTab } : file,
        );
        reset({});
        setFile(currentGridList[currentFileIdx - 1]);
        setFileDataInStore(currentGridList[currentFileIdx - 1]);
        setValue('date', '');
        setValue('time', '');
        setInitialValues({});
      }
    },
    [
      currentGridList,
      file,
      gridDisplayedTab,
      hasTabValueMismatch,
      reset,
      setValue,
      saveFileData,
      setFileDataInStore,
      isExhibitBlockedImmediate,
    ],
  );

  const nextClicked = useCallback(
    (data) => {
      if (isExhibitBlockedImmediate(data?.exhibitNumber)) return;
      const currentFileIdx = currentGridList.findIndex(
        (doc) => doc.folderFileId === file.folderFileId && doc.id === file.id,
      );
      if (currentFileIdx < totalDocuments - 1) {
        setLoading(true);
        setDisableActionBtns({ disableNext: true, disablePrev: true });
        saveFileData(
          data,
          currentGridList[currentFileIdx + 1].folderFileId ||
            currentGridList[currentFileIdx + 1].id,
          hasTabValueMismatch,
          hasTabValueMismatch ? { ...file, tab: gridDisplayedTab } : file,
        );
        reset({});
        setFile(currentGridList[currentFileIdx + 1]);
        setFileDataInStore(currentGridList[currentFileIdx + 1]);
        setInitialValues({});
      }
    },
    [
      currentGridList,
      file,
      gridDisplayedTab,
      hasTabValueMismatch,
      reset,
      saveFileData,
      totalDocuments,
      setFileDataInStore,
      isExhibitBlockedImmediate,
    ],
  );

  const saveFileData = useCallback(
    (data, newDocId, forceSave = false, baselineDoc = file) => {
      const isDocTime = !!data.docTime;
      const hasStartPageInData =
        data.startPage !== '' && data.startPage !== null && data.startPage !== undefined;
      const shouldForceSave =
        forceSave || (isTrialBookAgreed && !file.agreed && hasStartPageInData);
      const cleanData = {
        ...Object.entries(data).reduce(
          (a, [k, v]) =>
            (v === '' && !file[k]) || (isDocTime && k === 'docTime')
              ? a
              : isDocTime && k === 'docDate'
                ? { ...a, [k]: v + ' ' + data['docTime'] }
                : { ...a, [k]: v },
          {},
        ),
        ...{ etag: file.etag },
      };
      const noChanges = isEqual({ ...baselineDoc, ...cleanData }, baselineDoc);
      if (noChanges && newDocId && !shouldForceSave) selectNewDocument(newDocId);
      else if (!noChanges || shouldForceSave) handleSave(null, action, [baselineDoc], cleanData);
    },
    [action, file, handleSave, isTrialBookAgreed, selectNewDocument],
  );

  const isValidDate = (val) => {
    if (val) return moment(val, 'DD/MM/YYYY', true).isValid();
    else return true;
  };

  const isValidTime = (val) => {
    if (val) return moment(val, 'HH:mm:ss', true).isValid();
    else return true;
  };

  const areAllGroupsEmpty = (groups) => {
    return Object.values(groups).every((group) => group.length === 0);
  };

  const handleAddRecipient = () => {
    if (newRecipientVal) {
      const newRecipients = [...recipientListVal, newRecipientVal];
      setRecipientListVal(newRecipients);
      setValue('recipientsList', newRecipients);
      setNewRecipientVal('');
    }
  };

  const setRecipientsList = (suggestedRecipientsOption) => {
    if (suggestedRecipientsOption === suggestedRecipientsMenuOptions.addAll) {
      const newRecipients = [
        ...new Set([...recipientListVal, ...(file.suggestedRecipients || [])]),
      ];
      setLoading(true);
      setRecipientListVal(newRecipients);
      setValue('recipientsList', newRecipients);
      setTimeout(() => setLoading(false), 0);
    } else {
      setLoading(true);
      setRecipientListVal([]);
      setValue('recipientsList', []);
      setTimeout(() => setLoading(false), 0);
    }
    setRecipientVal(suggestedRecipientsOption);
  };

  const allowOnlyNumbersAndDots = (event) => {
    // If user presses Ctrl/Cmd + one of these, allow it
    if (
      (event.ctrlKey || event.metaKey) &&
      ['v', 'c', 'x', 'a'].includes(event.key.toLowerCase())
    ) {
      return;
    }

    // If user uses Shift+Insert (common paste), allow it
    if (event.shiftKey && event.key === 'Insert') {
      return;
    }

    const allowedKeys = [
      '.',
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];
    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <Modal
      show={show}
      title={T.translate('case.updateMetadata') + ' - ' + file.id}
      wide={showPreview}
      handleClose={() => {
        handleClose();
        setNavigationBtnClicked(false);
        setCurrentSelection(null);
      }}
      handleSave={handleSubmit((data) => {
        if (isExhibitBlockedImmediate(data?.exhibitNumber)) {
          return;
        }
        saveFileData(
          data,
          null,
          hasTabValueMismatch,
          hasTabValueMismatch ? { ...file, tab: gridDisplayedTab } : file,
        );
        handleClose();
        setNavigationBtnClicked(false);
        setCurrentSelection(null);
      })}
      saveTitle={T.translate('generic.update')}
      disableSave={
        (!isDirty && !hasTabValueMismatch && !shouldForceStartPageUpdate) ||
        exhibitCheckPending ||
        exhibitTaken ||
        !!errors.exhibitNumber
      }
      buttons={
        <>
          <Button
            type="button"
            id="updateFilePrevButton"
            onClick={handleSubmit((data) => prevClicked(data))}
            variant="contained"
            color="primary"
            disabled={disableActionBtns.disablePrev || exhibitBlocked}
          >
            {T.translate('generic.previous')}
          </Button>
          <Button
            type="button"
            id="updateFileNextButton"
            onClick={handleSubmit((data) => nextClicked(data))}
            variant="contained"
            color="primary"
            disabled={disableActionBtns.disableNext || exhibitBlocked}
          >
            {T.translate('generic.next')}
          </Button>
        </>
      }
      hintContent={
        <>
          <span>
            <b>{T.translate('generic.shortcuts')}</b>
          </span>
          <Divider />
          <span>{T.translate('common.shortcutPrevText')}</span>
          <br />
          <span>{T.translate('common.shortcutNextText')}</span>
          <br />
          <span>{T.translate('common.shortcutSaveText')}</span>
        </>
      }
      draggableDialog
      fullHeight={showPreview}
    >
      {loading ? (
        <Spinner />
      ) : (
        <Split
          sizes={showPreview ? [45, 55] : [100, 0]}
          minSize={showPreview ? 100 : 0}
          expandToMin={false}
          gutterSize={10}
          gutterAlign="center"
          snapOffset={30}
          dragInterval={1}
          direction="horizontal"
          cursor="col-resize"
          className={classnames({ 'no-gutter': !showPreview }, 'case-table-and-viewer')}
        >
          <Form
            onSubmit={(e) => {
              e.preventDefault();
            }}
            style={{ ...(showPreview && { marginRight: '0.5rem' }) }}
          >
            <Form.Group style={{ margin: '0.5rem' }}>
              {canUpdatePublicData(file) && (
                <>
                  <Row style={{ marginBottom: 0 }}>
                    <Col>
                      {file.suggestedSubject && (
                        <SubjectLabel
                          isAuthor={false}
                          data={file.suggestedSubject}
                          setValue={setValue}
                          setSelectedVal={setFileNameVal}
                          value={fileNameVal}
                        />
                      )}
                    </Col>
                    <Col />
                  </Row>
                  <Row>
                    <Col>
                      {!(canChangeDocId && isAllDocuments) && (
                        <Button
                          sx={{ float: 'right', p: 0 }}
                          variant="text"
                          onClick={() => {
                            setLoading(true);
                            setShowPreview(!showPreview);
                            setTimeout(() => {
                              setLoading(false);
                            }, 0);
                            setShowPreview(!showPreview);
                          }}
                        >
                          {showPreview ? 'Hide' : 'Preview'}
                        </Button>
                      )}
                      <TextForm
                        {...register('name', {
                          required: true,
                          onChange: (e) => setFileNameVal(e.target.value),
                        })}
                        label={T.translate('case.systemName')}
                        placeholder={T.translate('case.namePlaceholder')}
                        value={fileNameVal}
                        disabled={isDocFinder}
                        autofocus={
                          !currentSelection || (navigationBtnClicked && currentSelection === 'name')
                        }
                        errors={errors}
                      />
                    </Col>
                    {canChangeDocId && isAllDocuments && (
                      <Col>
                        <Button
                          sx={{ float: 'right', p: 0 }}
                          variant="text"
                          onClick={() => {
                            setLoading(true);
                            setShowPreview(!showPreview);
                            setTimeout(() => {
                              setLoading(false);
                            }, 0);
                            setShowPreview(!showPreview);
                          }}
                        >
                          {showPreview ? 'Hide' : 'Preview'}
                        </Button>
                        <TextForm
                          {...register('id', { required: true })}
                          label={T.translate('case.documentId')}
                          placeholder={T.translate('case.documentIdPlaceholder')}
                          defaultValue={file.id}
                          errors={errors}
                        />
                      </Col>
                    )}
                  </Row>
                  {isTrialBookAgreed && (
                    <Row>
                      <Col>
                        <TextForm
                          {...register('tab', {
                            validate: {
                              isValidNumber: (val) => {
                                if (!val) return true;
                                const pattern = /^\d+(\.\d+)*$/;
                                return (
                                  pattern.test(val) ||
                                  'Invalid format. Only digits and (optional) multiple decimal segments allowed.'
                                );
                              },
                            },
                          })}
                          autofocus={navigationBtnClicked && currentSelection === 'tab'}
                          onKeyDown={allowOnlyNumbersAndDots}
                          label={T.translate('case.tab')}
                          placeholder={T.translate('case.tabPlaceholder')}
                          defaultValue={file.tab}
                          readOnly={file.agreed}
                          errors={errors}
                        />
                      </Col>
                      {currentFolder && currentFolder.globalPaging ? (
                        <Col>
                          <TextForm
                            {...register('startPage', {
                              validate: { isValidNumber: (val) => val >= 0 },
                            })}
                            label={T.translate('case.startPage')}
                            autofocus={navigationBtnClicked && currentSelection === 'startPage'}
                            type="number"
                            placeholder={T.translate('case.startPagePlaceholder')}
                            defaultValue={file.startPage}
                            readOnly={file.agreed}
                            errors={errors}
                          />
                        </Col>
                      ) : (
                        <Col />
                      )}
                    </Row>
                  )}
                  <Row style={{ marginBottom: 0 }}>
                    <Col>
                      <DateFormLabel
                        isAutoSetDocDate={isAutoSetDocDate}
                        dates={file.docDates}
                        trigger={trigger}
                        setValue={setValue}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Controller
                        name={'docDate'}
                        control={control}
                        rules={{
                          validate: {
                            isValidDate: (val) => isValidDate(val),
                          },
                          required: !!getValues('time') && T.translate('case.dateRequiredMsg'),
                        }}
                        render={({ field }) => (
                          <DateInput
                            {...field}
                            label={T.translate('case.dateWithFormat')}
                            placeholder={T.translate('case.datePlaceholder')}
                            errors={errors}
                            autofocus={navigationBtnClicked && currentSelection === 'docDate'}
                          />
                        )}
                      />
                    </Col>
                    <Col>
                      <Controller
                        name={'docTime'}
                        control={control}
                        rules={{
                          validate: {
                            isValidTime: (val) => isValidTime(val),
                          },
                        }}
                        render={({ field }) => (
                          <TimeInput
                            {...field}
                            label={T.translate('case.time')}
                            placeholder={T.translate('case.timePlaceholder')}
                            errors={errors}
                            autofocus={navigationBtnClicked && currentSelection === 'docTime'}
                          />
                        )}
                      />
                    </Col>
                    <Col>
                      <TextForm
                        {...register('altDate')}
                        label={T.translate('case.alternativeDate')}
                        placeholder={T.translate('case.alternativeDatePlaceholder')}
                        defaultValue={file.altDate}
                        autofocus={navigationBtnClicked && currentSelection === 'altDate'}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <TextForm
                        {...register('disclosureId')}
                        label={T.translate('case.disclosureId')}
                        placeholder={T.translate('case.disclosureIdPlaceholder')}
                        defaultValue={file.disclosureId}
                        autofocus={navigationBtnClicked && currentSelection === 'disclosureId'}
                      />
                    </Col>
                    <Col>
                      <TextForm
                        {...register('familyId')}
                        label={T.translate('case.familyId')}
                        placeholder={T.translate('case.familyIdPlaceholder')}
                        defaultValue={file.familyId}
                        autofocus={navigationBtnClicked && currentSelection === 'familyId'}
                      />
                    </Col>
                    <Col />
                  </Row>
                  <Row>
                    <Col>
                      <TextForm
                        {...register('discoveryId')}
                        label={T.translate('case.discoveryId')}
                        placeholder={T.translate('case.discoveryIdPlaceholder')}
                        defaultValue={file.discoveryId}
                        autofocus={navigationBtnClicked && currentSelection === 'discoveryId'}
                      />
                    </Col>
                    <Col>
                      <TextForm
                        {...register('externalId')}
                        label={T.translate('case.externalId')}
                        placeholder={T.translate('case.externalIdPlaceholder')}
                        defaultValue={file.externalId}
                        autofocus={navigationBtnClicked && currentSelection === 'externalId'}
                      />
                    </Col>
                    {showExhibitNumber && (
                      <Col>
                        <TextForm
                          {...register('exhibitNumber', {
                            validate: (val) =>
                              !val ||
                              EXHIBIT_NUMBER_REGEX.test(val.trim()) ||
                              T.translate('case.exhibitNumberInvalidFormat'),
                          })}
                          label={T.translate('case.exhibitNumber')}
                          placeholder={T.translate('case.exhibitNumberPlaceholder')}
                          defaultValue={file.exhibitNumber}
                          autofocus={navigationBtnClicked && currentSelection === 'exhibitNumber'}
                          onKeyDown={handleExhibitKeyDown}
                          onPaste={handleExhibitPaste}
                          onChange={handleExhibitChange}
                        />
                        {exhibitCheckPending && (
                          <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                            {T.translate('case.exhibitNumberChecking')}
                          </div>
                        )}
                        {exhibitTaken && !exhibitCheckPending && (
                          <div style={{ color: '#d32f2f', fontSize: '0.85rem' }}>
                            {T.translate('case.exhibitNumberTaken')}
                          </div>
                        )}
                        {exhibitErrorMessage && !exhibitTaken && !exhibitCheckPending && (
                          <div style={{ color: '#d32f2f', fontSize: '0.85rem' }}>
                            {exhibitErrorMessage}
                          </div>
                        )}
                      </Col>
                    )}
                  </Row>
                  <Row style={{ marginBottom: 0 }}>
                    <Col>
                      {file.suggestedAuthors && file.suggestedAuthors.length > 0 && (
                        <AuthorRecipientLabel
                          isAuthor={true}
                          data={file.suggestedAuthors}
                          setValue={setValue}
                          setSelectedVal={setAuthorVal}
                          value={authorVal}
                        />
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <TextForm
                        {...register('author', { onChange: (e) => setAuthorVal(e.target.value) })}
                        label={T.translate('case.author')}
                        placeholder={T.translate('case.authorPlaceholder')}
                        value={authorVal}
                        autofocus={navigationBtnClicked && currentSelection === 'author'}
                      />
                    </Col>
                    <Col />
                  </Row>
                  <Row style={{ marginBottom: 0 }}>
                    <Col>
                      {file.suggestedRecipients && file.suggestedRecipients.length > 0 && (
                        <AuthorRecipientLabel
                          isAuthor={false}
                          data={file.suggestedRecipients}
                          setValue={setValue}
                          setSelectedVal={setRecipientVal}
                          value={recipientVal}
                          setRecipientsList={setRecipientsList}
                        />
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          marginBottom: '0.5rem',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>{T.translate('case.recipient')}</span>
                        <TextField
                          variant="standard"
                          size="small"
                          autoComplete="off"
                          placeholder={T.translate('case.newRecipientPlaceholder')}
                          value={newRecipientVal}
                          onChange={(e) => setNewRecipientVal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newRecipientVal.length > 0) {
                              e.preventDefault();
                              e.stopPropagation();
                              handleAddRecipient();
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={handleAddRecipient}
                                  edge="end"
                                  disabled={!newRecipientVal}
                                >
                                  {
                                    <AddCircleOutline
                                      color={!newRecipientVal ? 'disabled' : 'primary'}
                                    />
                                  }
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </div>
                      <MultiSelectForm
                        name="recipientsList"
                        defaultValue={recipientListVal}
                        options={recipientListVal.map((val) => ({
                          label: val,
                          value: val,
                        }))}
                        control={control}
                        error={errors}
                        autofocus={recipientListVal.length !== prevRecipientListVal?.length}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <TextForm
                        {...register('description')}
                        label={T.translate('case.description')}
                        multiline
                        placeholder={T.translate('case.descriptionPlaceholder')}
                        defaultValue={file.description}
                        autofocus={navigationBtnClicked && currentSelection === 'description'}
                      />
                    </Col>
                  </Row>
                </>
              )}
              {canUpdateTeamData(file) && (
                <Row>
                  <Col>
                    <TextForm
                      {...register('notes')}
                      multiline
                      label={`${T.translate('case.notes')} (${T.translate('case.privateToTeam')})`}
                      placeholder={T.translate('case.notesPlaceholder')}
                      defaultValue={file.notes}
                      autofocus={navigationBtnClicked && currentSelection === 'notes'}
                    />
                  </Col>
                </Row>
              )}
              {entitiesCollection && !areAllGroupsEmpty(entitiesCollection) && (
                <>
                  <Row>
                    <Col style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ width: '100%' }}>{T.translate('generic.entities')}</span>
                      <IconButton onClick={() => setOpenEntities(!openEntities)}>
                        {openEntities ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Col>
                  </Row>
                  <Collapse in={openEntities} timeout="auto" unmountOnExit>
                    {Object.entries(entitiesCollection).map(
                      ([key, value]) =>
                        value &&
                        value.length > 0 && (
                          <Row key={key}>
                            <Col>
                              <MultiSelectForm
                                label={key}
                                name={key}
                                defaultValue={value}
                                options={value.map((val) => ({
                                  label: val,
                                  value: val,
                                  isFixed: true,
                                }))}
                                readOnly
                                control={control}
                                error={errors}
                              />
                            </Col>
                          </Row>
                        ),
                    )}
                  </Collapse>
                </>
              )}
            </Form.Group>
          </Form>
          {showPreview && (
            <div>
              <FileViewer fileId={file.id} isPreviewMode />
            </div>
          )}
        </Split>
      )}
    </Modal>
  );
};

export default ModalUpdateMeta;
